import React, { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { getPackages, getQuotes, runBidLeveling, getPackageBidLeveling, uploadQuote, getSuppliers } from '../utils/api';
import LifecyclePill from '../components/LifecyclePill';

const fmt = n => n ? '£' + Number(n).toLocaleString() : '—';

export default function BidLeveling() {
  const { selectedProject, addToast } = useOutletContext();
  const [packages, setPackages] = useState([]);
  const [selectedPkg, setSelectedPkg] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [bidLeveling, setBidLeveling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [leveling, setLeveling] = useState(false);
  const [showAddQuote, setShowAddQuote] = useState(false);
  const [quoteForm, setQuoteForm] = useState({ supplier_id: '', total_price: '', lead_time_days: '', exclusions: '', payment_terms: '', validity_date: '' });
  const [addingQuote, setAddingQuote] = useState(false);
  const [message, setMessage] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadingSupplierId, setUploadingSupplierId] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (selectedProject?.id) loadAll();
  }, [selectedProject]);

  useEffect(() => {
    if (selectedPkg) loadPackageData(selectedPkg.id);
  }, [selectedPkg]);

  async function loadAll() {
    setLoading(true);
    try {
      const [pkgsData, suppliersData] = await Promise.all([
        getPackages(selectedProject.id),
        getSuppliers()
      ]);
      setPackages(pkgsData || []);
      setSuppliers(suppliersData || []);
      if (pkgsData?.length > 0 && !selectedPkg) setSelectedPkg(pkgsData[0]);
    } catch (err) { console.error(err); }
    setLoading(false);
  }

  async function loadPackageData(packageId) {
    try {
      const [quotesData, blData] = await Promise.all([
        getQuotes(packageId),
        getPackageBidLeveling(packageId).catch(() => null)
      ]);
      setQuotes(quotesData || []);
      setBidLeveling(blData);
    } catch (err) { console.error(err); }
  }

  async function handleRunBidLeveling() {
    if (!selectedPkg) return;
    setLeveling(true);
    try {
      const result = await fetch(
        `${process.env.REACT_APP_API_URL || 'https://procura-backend-0yj4.onrender.com'}/api/quotes/package/${selectedPkg.id}/level`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + localStorage.getItem('token')
          }
        }
      ).then(r => r.json());

      setBidLeveling(result);
      setMessage({ type: 'success', text: '✦ AI bid leveling complete' });
      await loadPackageData(selectedPkg.id);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
    setLeveling(false);
  }

  async function handleAddQuote(e) {
    e.preventDefault();
    setAddingQuote(true);
    try {
      await fetch(
        `${process.env.REACT_APP_API_URL || 'https://procura-backend-0yj4.onrender.com'}/api/quotes`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + localStorage.getItem('token')
          },
          body: JSON.stringify({ ...quoteForm, package_id: selectedPkg.id })
        }
      ).then(r => r.json());

      setShowAddQuote(false);
      setQuoteForm({ supplier_id: '', total_price: '', lead_time_days: '', exclusions: '', payment_terms: '', validity_date: '' });
      setMessage({ type: 'success', text: 'Quote added successfully' });
      await loadPackageData(selectedPkg.id);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
    setAddingQuote(false);
  }

  async function handleUploadQuote() {
    if (!uploadFile || !uploadingSupplierId) {
      setMessage({ type: 'error', text: 'Select a supplier and a file' });
      return;
    }
    const formData = new FormData();
    formData.append('file', uploadFile);
    formData.append('package_id', selectedPkg.id);
    formData.append('supplier_id', uploadingSupplierId);

    try {
      setAddingQuote(true);
      await uploadQuote(selectedPkg.id, formData);
      setUploadFile(null);
      setUploadingSupplierId('');
      setMessage({ type: 'success', text: '✦ Quote uploaded and parsed by AI' });
      await loadPackageData(selectedPkg.id);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
    setAddingQuote(false);
  }

  const analysis = bidLeveling?.analysis || (bidLeveling?.comparison_json ? (typeof bidLeveling.comparison_json === 'string' ? JSON.parse(bidLeveling.comparison_json) : bidLeveling.comparison_json) : null);

  if (loading) return <div className="page"><div className="empty-state"><div className="spinner" /></div></div>;

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header-left">
          <div className="page-title">Bid Leveling</div>
          <div className="page-subtitle">Compare quotes, flag exclusions, identify best value</div>
        </div>
        <div className="page-actions">
          {selectedPkg && quotes.length > 0 && (
            <button onClick={handleRunBidLeveling} disabled={leveling} className="btn btn-primary">
              {leveling ? '✦ AI Analyzing...' : '✦ Run AI Bid Leveling'}
            </button>
          )}
          {selectedPkg && (
            <button onClick={() => setShowAddQuote(!showAddQuote)} className="btn btn-secondary">
              + Add Quote
            </button>
          )}
        </div>
      </div>

      {message && (
        <div style={{ padding: '12px 16px', borderRadius: 'var(--radius-md)', fontSize: '0.875rem', fontWeight: 500, marginBottom: 16, background: message.type === 'success' ? '#ECFDF5' : '#FEF2F2', color: message.type === 'success' ? 'var(--color-success)' : 'var(--color-error)', border: `1px solid ${message.type === 'success' ? 'var(--color-success)' : 'var(--color-error)'}`, display: 'flex', justifyContent: 'space-between' }}>
          {message.text}
          <button onClick={() => setMessage(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', opacity: 0.5 }}>✕</button>
        </div>
      )}

      {packages.length === 0 ? (
        <div className="empty-state card" style={{ padding: 48 }}>
          <div className="empty-state-icon">⚖</div>
          <h3>No packages yet</h3>
          <p>Create packages and receive quotes to start bid leveling</p>
          <button onClick={() => navigate('/packages')} className="btn btn-primary">Go to Packages →</button>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 16 }}>

          {/* Package selector */}
          <div className="card">
            <div className="card-header">
              <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>Packages ({packages.length})</span>
            </div>
            {packages.map(pkg => {
              const isActive = selectedPkg?.id === pkg.id;
              const pkgQuoteCount = pkg.id === selectedPkg?.id ? quotes.length : 0;
              return (
                <div key={pkg.id} onClick={() => setSelectedPkg(pkg)}
                  style={{
                    padding: '12px 16px', cursor: 'pointer',
                    background: isActive ? 'var(--color-teal-light)' : 'transparent',
                    borderLeft: `3px solid ${isActive ? 'var(--color-teal)' : 'transparent'}`,
                    borderBottom: '1px solid var(--color-border)',
                  }}
                  onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'var(--color-background)'; }}
                  onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                >
                  <div style={{ fontSize: '0.8125rem', fontWeight: 600, marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pkg.name}</div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <LifecyclePill state={pkg.lifecycle_state} />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Main content */}
          {selectedPkg && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

              {/* Add quote form */}
              {showAddQuote && (
                <div className="card">
                  <div className="card-header">
                    <span style={{ fontWeight: 600 }}>Add Quote</span>
                    <button onClick={() => setShowAddQuote(false)} className="btn btn-ghost btn-sm">✕</button>
                  </div>
                  <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

                    {/* Upload quote document */}
                    <div>
                      <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--color-text-light)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
                        ✦ Upload Quote Document (AI will parse)
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <div className="form-group">
                          <label className="form-label">Supplier</label>
                          <select className="form-select" value={uploadingSupplierId} onChange={e => setUploadingSupplierId(e.target.value)}>
                            <option value="">Select supplier...</option>
                            {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                          </select>
                        </div>
                        <div className="form-group">
                          <label className="form-label">Quote Document (PDF, Excel, Word)</label>
                          <input type="file" accept=".pdf,.xlsx,.xls,.docx,.doc,.txt" className="form-input"
                            onChange={e => setUploadFile(e.target.files[0])}
                            style={{ padding: '6px 12px' }}
                          />
                        </div>
                        <button onClick={handleUploadQuote} disabled={addingQuote || !uploadFile || !uploadingSupplierId} className="btn btn-primary btn-sm">
                          {addingQuote ? '✦ AI Parsing...' : '✦ Upload & Parse with AI'}
                        </button>
                      </div>
                    </div>

                    <div className="divider" />

                    {/* Manual entry */}
                    <div>
                      <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--color-text-light)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
                        Manual Entry
                      </div>
                      <form onSubmit={handleAddQuote}>
                        <div className="grid-2" style={{ marginBottom: 12 }}>
                          <div className="form-group">
                            <label className="form-label">Supplier *</label>
                            <select className="form-select" value={quoteForm.supplier_id} onChange={e => setQuoteForm({ ...quoteForm, supplier_id: e.target.value })} required>
                              <option value="">Select supplier...</option>
                              {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                            </select>
                          </div>
                          <div className="form-group">
                            <label className="form-label">Total Price (£) *</label>
                            <input type="number" className="form-input" value={quoteForm.total_price} onChange={e => setQuoteForm({ ...quoteForm, total_price: e.target.value })} placeholder="125000" required />
                          </div>
                          <div className="form-group">
                            <label className="form-label">Lead Time (days)</label>
                            <input type="number" className="form-input" value={quoteForm.lead_time_days} onChange={e => setQuoteForm({ ...quoteForm, lead_time_days: e.target.value })} placeholder="45" />
                          </div>
                          <div className="form-group">
                            <label className="form-label">Valid Until</label>
                            <input type="date" className="form-input" value={quoteForm.validity_date} onChange={e => setQuoteForm({ ...quoteForm, validity_date: e.target.value })} />
                          </div>
                          <div className="form-group">
                            <label className="form-label">Payment Terms</label>
                            <input className="form-input" value={quoteForm.payment_terms} onChange={e => setQuoteForm({ ...quoteForm, payment_terms: e.target.value })} placeholder="30 days net" />
                          </div>
                          <div className="form-group">
                            <label className="form-label">Exclusions</label>
                            <input className="form-input" value={quoteForm.exclusions} onChange={e => setQuoteForm({ ...quoteForm, exclusions: e.target.value })} placeholder="VAT excluded. Groundworks not included." />
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button type="submit" disabled={addingQuote} className="btn btn-primary btn-sm">{addingQuote ? 'Adding...' : 'Add Quote'}</button>
                          <button type="button" onClick={() => setShowAddQuote(false)} className="btn btn-secondary btn-sm">Cancel</button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              )}

              {/* Quotes table */}
              <div className="card">
                <div className="card-header">
                  <span style={{ fontWeight: 600 }}>Quotes Received ({quotes.length})</span>
                  {quotes.length === 0 && (
                    <button onClick={() => setShowAddQuote(true)} className="btn btn-primary btn-sm">+ Add Quote</button>
                  )}
                </div>
                {quotes.length === 0 ? (
                  <div className="empty-state" style={{ padding: 40 }}>
                    <div className="empty-state-icon">⚖</div>
                    <h3>No quotes yet</h3>
                    <p>Add quotes manually or upload quote documents for AI parsing</p>
                    <button onClick={() => setShowAddQuote(true)} className="btn btn-primary btn-sm">+ Add First Quote</button>
                  </div>
                ) : (
                  <div className="table-wrapper">
                    <table>
                      <thead>
                        <tr>
                          {['Supplier', 'Total Price', 'Lead Time', 'Valid Until', 'Payment Terms', 'Exclusions', 'Status'].map(h => <th key={h}>{h}</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {quotes.map((q, i) => {
                          const isBest = analysis?.best_price_supplier && q.supplier_name?.toLowerCase().includes(analysis.best_price_supplier.toLowerCase());
                          return (
                            <tr key={q.id} style={{ background: isBest ? '#F0FDF4' : 'transparent' }}>
                              <td>
                                <div style={{ fontWeight: 600 }}>{q.supplier_name || `Supplier #${q.supplier_id}`}</div>
                                {isBest && <span className="badge badge-success" style={{ fontSize: '0.6875rem' }}>Best Price</span>}
                              </td>
                              <td style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, color: isBest ? 'var(--color-success)' : 'var(--color-text-dark)' }}>
                                {fmt(q.total_price)}
                              </td>
                              <td style={{ fontFamily: 'var(--font-mono)' }}>{q.lead_time_days ? `${q.lead_time_days}d` : '—'}</td>
                              <td style={{ fontSize: '0.8125rem' }}>{q.validity_date ? new Date(q.validity_date).toLocaleDateString() : '—'}</td>
                              <td style={{ fontSize: '0.8125rem', color: 'var(--color-text-mid)' }}>{q.payment_terms || '—'}</td>
                              <td>
                                {q.exclusions ? (
                                  <span style={{ fontSize: '0.75rem', color: 'var(--color-warning)', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4 }}>
                                    ⚑ {q.exclusions.slice(0, 40)}{q.exclusions.length > 40 ? '...' : ''}
                                  </span>
                                ) : <span style={{ fontSize: '0.75rem', color: 'var(--color-text-light)' }}>None stated</span>}
                              </td>
                              <td>
                                <span className={`badge ${q.parse_status === 'processed' ? 'badge-success' : q.parse_status === 'manual' ? 'badge-info' : 'badge-neutral'}`}>
                                  {q.parse_status || 'pending'}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Bid leveling results */}
              {analysis && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

                  {/* Summary */}
                  <div className="card">
                    <div className="card-header">
                      <div>
                        <span style={{ fontWeight: 600 }}>AI Bid Leveling Analysis</span>
                        <span className="ai-badge" style={{ marginLeft: 8 }}>✦ AI Analysis</span>
                      </div>
                      <button onClick={() => navigate(`/packages/${selectedPkg.id}`)} className="btn btn-secondary btn-sm">
                        Open Package →
                      </button>
                    </div>
                    <div className="card-body">
                      {analysis.summary && (
                        <div style={{ padding: '12px 14px', background: 'var(--color-teal-light)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-teal)', fontSize: '0.875rem', lineHeight: 1.6, color: 'var(--color-text-dark)', marginBottom: 16 }}>
                          {analysis.summary}
                        </div>
                      )}

                      {/* Winners */}
                      <div className="grid-3" style={{ marginBottom: 16 }}>
                        {[
                          { label: 'Best Price', value: analysis.best_price_supplier, color: 'var(--color-success)' },
                          { label: 'Best Value', value: analysis.best_value_supplier, color: 'var(--color-teal)' },
                          { label: 'Fastest', value: analysis.fastest_supplier, color: 'var(--color-info)' },
                        ].map(item => (
                          <div key={item.label} style={{ padding: '14px', background: 'var(--color-background)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)', borderLeft: `3px solid ${item.color}` }}>
                            <div style={{ fontSize: '0.75rem', color: 'var(--color-text-light)', marginBottom: 6 }}>{item.label}</div>
                            <div style={{ fontWeight: 700, color: item.color, fontSize: '0.9375rem' }}>{item.value || '—'}</div>
                          </div>
                        ))}
                      </div>

                      {/* Risk flags */}
                      {analysis.risk_flags?.length > 0 && (
                        <div style={{ marginBottom: 16 }}>
                          <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--color-text-light)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Risk Flags</div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {analysis.risk_flags.map((flag, i) => (
                              <div key={i} className={`risk-flag risk-flag-${flag.severity || 'medium'}`}>
                                <span>⚑</span>
                                <div>
                                  <div style={{ fontWeight: 600, fontSize: '0.8125rem' }}>{flag.supplier}</div>
                                  <div style={{ fontSize: '0.8125rem' }}>{flag.risk}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Exclusion flags */}
                      {analysis.exclusion_flags?.length > 0 && (
                        <div style={{ marginBottom: 16 }}>
                          <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--color-text-light)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Exclusion Flags</div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {analysis.exclusion_flags.map((flag, i) => (
                              <div key={i} style={{ padding: '10px 14px', background: 'var(--color-warning-bg)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-warning)', borderLeft: '3px solid var(--color-warning)', display: 'flex', gap: 10 }}>
                                <span style={{ color: 'var(--color-warning)' }}>⚑</span>
                                <div>
                                  <div style={{ fontWeight: 600, fontSize: '0.8125rem', color: 'var(--color-warning)' }}>{flag.supplier} — {flag.impact} impact</div>
                                  <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-dark)' }}>{flag.exclusion}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Comparable totals */}
                      {analysis.comparable_totals?.length > 0 && (
                        <div style={{ marginBottom: 16 }}>
                          <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--color-text-light)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Adjusted Comparable Totals</div>
                          <div className="table-wrapper">
                            <table>
                              <thead>
                                <tr>{['Supplier', 'Stated Price', 'Adjusted Price', 'Adjustment'].map(h => <th key={h}>{h}</th>)}</tr>
                              </thead>
                              <tbody>
                                {analysis.comparable_totals.map((ct, i) => (
                                  <tr key={i}>
                                    <td style={{ fontWeight: 600 }}>{ct.supplier}</td>
                                    <td style={{ fontFamily: 'var(--font-mono)' }}>{fmt(ct.stated_price)}</td>
                                    <td style={{ fontFamily: 'var(--font-mono)', fontWeight: 700 }}>{fmt(ct.adjusted_price)}</td>
                                    <td style={{ fontSize: '0.8125rem', color: 'var(--color-text-mid)' }}>{ct.adjustment_reason || '—'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Recommendation */}
                      {analysis.recommendation && (
                        <div style={{ padding: '14px 16px', background: '#ECFDF5', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-success)' }}>
                          <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--color-success)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>AI Recommendation</div>
                          <div style={{ fontSize: '0.875rem', lineHeight: 1.7, color: 'var(--color-text-dark)' }}>{analysis.recommendation}</div>
                          <button onClick={() => navigate(`/packages/${selectedPkg.id}?tab=award`)} className="btn btn-primary btn-sm" style={{ marginTop: 12 }}>
                            → Generate Award Memo
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* No bid leveling yet */}
              {!analysis && quotes.length > 0 && (
                <div className="card">
                  <div className="card-body" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: 40, textAlign: 'center' }}>
                    <span className="ai-badge">✦ AI Bid Leveling</span>
                    <h3>{quotes.length} quote{quotes.length !== 1 ? 's' : ''} received — ready to level</h3>
                    <p style={{ maxWidth: 400 }}>AI will compare all quotes like-for-like, flag exclusions and risks, adjust comparable totals, and recommend the best value option.</p>
                    <button onClick={handleRunBidLeveling} disabled={leveling} className="btn btn-primary">
                      {leveling ? '✦ AI Analyzing...' : '✦ Run AI Bid Leveling'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}